import React, {ChangeEvent, DragEventHandler, useContext, useRef, useState} from 'react'
import {FileArrowUp, Information} from 'shared/ui/icons'
import {WarningModalContext, WarningModalContextType} from '../warningModal/context'
import styles from './styles.module.scss'

const ACTIVE_DRAG_COLOR = 'rgba(24, 118, 242, 0.1)'

type MediaUploadProps = {
  width?: number | string,
  height?: number | string,
  title?: string,
  fileLimit?: string,
  fileLimitText?: string,
  fileSizeMB?: number,
  name?: string,
  multiple?: boolean,
  required?: boolean
  isRequiredLabel?: boolean,
  disabled?: boolean,
  setImages?: (name: string, files: File[], multiple: boolean | undefined) => void,
  tooltip?: {
    title?: string,
    description?: string
  }
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
  width,
  height = 114,
  fileLimit,
  fileLimitText,
  title,
  fileSizeMB = 6,
  name = 'test',
  multiple,
  required,
  isRequiredLabel = true,
  tooltip,
  setImages,
  disabled
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [isShowTooltip, setIsShowTooltip] = useState(false)
  const {setWarningModal} = useContext(WarningModalContext) as WarningModalContextType

  const onClickHandler = () => {
    !disabled && inputRef.current?.click()
  }

  const onChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (!(e.target.files && e.target.files[0])) return setWarningModal({header: 'Error', text: 'Error: not files'})

    setImages && setImages(name, Array.from(e.target.files), multiple)
    if(inputRef.current) inputRef.current.value = ''
  }

  const handleDrag: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(e.type !== 'dragleave')
  }

  const onDropHandler: DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(false)

    if (!e.dataTransfer.files) return

    setImages && setImages(name, Array.from(e.dataTransfer.files), multiple)
  }

  return (
    <div className={styles.wrapper} style={{width}}>
      {title || tooltip ? <div className={styles.mediaUpload__titleWrapper}>
        {title ? <div className={styles.mediaUpload__titleContainer}>
          <p className={styles.mediaUpload__title}>{title}</p>
          {isRequiredLabel ? <span className={styles.mediaUpload__title_required}>*</span> : null}
        </div> : null}

        {tooltip ? <>
          <div
            onMouseOver={() => setIsShowTooltip(true)}
            onMouseLeave={() => setIsShowTooltip(false)}
            className={styles.mediaUpload__informationIcon}>
            <Information />
          </div>
          {(tooltip.title || tooltip.description) && isShowTooltip ? <TitleTooltip {...tooltip} /> : null}
        </> : null}
      </div> : null}

      <div
        className={styles.mediaUpload}
        style={{height, background: dragActive ? ACTIVE_DRAG_COLOR : ''}}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={onDropHandler}
        onClick={onClickHandler}
      >
        <input
          ref={inputRef}
          type='file'
          accept={fileLimit || '.jpg, .png, .gif, .jpeg'}
          onChange={onChangeHandler}
          multiple={multiple}
          required={required}
          className={styles.mediaUpload__fileInput}
        />
        <div className={styles.mediaUpload__innerWrapper}>
          <div className={styles.mediaUpload__fileIcon}>
            <FileArrowUp />
          </div>
          <span className={styles.mediaUpload__limitDescription}>{fileLimitText || `JPG, GIF, PNG Max ${fileSizeMB}MB`}</span>
        </div>
      </div>
    </div>
  )
}

type TitleTooltipProps = {
  title?: string,
  description?: string
}

const TitleTooltip: React.FC<TitleTooltipProps> = ({title, description}) => {
  return (
    <div className={styles.tooltip}>
      {title ? <p className={styles.tooltip__title}>{title}</p> : null}
      {description ? <span className={styles.tooltip__description}>{description}</span> : null}
    </div>
  )
}